import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Listbox, Transition } from '@headlessui/react';
import { ListboxOptions } from '../SelectInput/components/ListboxOptions';

import { Modal } from './components/Modal';
import { S_ChevronDown, S_ChevronUp } from 'hosman-material';

export const SelectInput = ({
  name,
  options,
  selectedOption,
  placeholder = 'Choisir une réponse',
  label,
  handleChange,
  disabled = false,
  listHeight = 'h-fit max-h-full md:max-h-[200px]',
  autoScroll = true
}) => {
  const [modal, setModal] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  const handleWindowSize = () => {
    setIsDesktop(window.innerWidth > 768);
  };

  useEffect(() => {
    window.addEventListener('resize', handleWindowSize);
    handleWindowSize();
  }, []);

  const handleClick = (e) => {
    setModal(true);
    const buttonTarget = e.currentTarget;

    if (!autoScroll) return;
    // setTimeout is needed to wait for the modal to open
    setTimeout(() => {
      if (buttonTarget.nextElementSibling) {
        const rect = buttonTarget.nextElementSibling.getBoundingClientRect();
        const topPosition = rect.top + window.scrollY;
        const viewportHeight = window.innerHeight;
        const elementCenterPosition = topPosition - viewportHeight / 2;
        window.scrollTo({ top: elementCenterPosition, behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div>
      {label && (
        <div className='ont-semibold text-ih-indigo mb-3'>
          <label htmlFor={name}>{label}</label>
        </div>
      )}

      <Listbox
        as='div'
        className={`relative focus-within:z-50 font-medium text-sm w-full ${
          disabled ? 'opacity-40' : ''
        }`}
        value={selectedOption}
        onChange={(event) => handleChange(event)}
        disabled={disabled}
      >
        {({ open }) => (
          <div className='w-[-webkit-fill-available]'>
            <Listbox.Button
              data-testid='select-input'
              onClick={(e) => handleClick(e)}
              className={`block w-full h-12 bg-white/10 bg-opacity-30 text-white rounded-2xl border-white/20 hover:border-white/40 p-3 focus:ring-0 focus:border-white/40 backdrop-blur-sm ${
                disabled
                  ? 'cursor-not-allowed'
                  : options.length > 0
                  ? 'cursor-pointer'
                  : 'cursor-default'
              } w-full flex justify-between items-center border ${
                open
                  ? 'border-ih-blue'
                  : ' border-ih-purple-30 hover:border-ih-purple-40'
              } transition ease-in-out duration-150 bg-white`}
            >
              <div
                className={`${
                  selectedOption ? 'text-white' : 'text-white/30'
                } 'ml-2 truncate`}
              >
                {selectedOption ? selectedOption.name : placeholder}
              </div>
              {options.length > 0 && (
                <FontAwesomeIcon
                  icon={open ? S_ChevronUp : S_ChevronDown}
                  className='text-white ml-2 w-4 h-4'
                  data-testid='select-input-chevron'
                />
              )}
            </Listbox.Button>

            {options.length > 0 && !isDesktop && (
              <Transition
                show={open}
                leave='transition duration-200 ease-in-out'
                leaveFrom='transform opacity-100'
                leaveTo='transform opacity-0'
              >
                <Modal
                  isOpen={modal}
                  closeModal={() => setModal(false)}
                  width='w-full'
                  bgOpacity='bg-opacity-10'
                >
                  <ListboxOptions options={options} listHeight={listHeight} />
                </Modal>
              </Transition>
            )}

            {options.length > 0 && isDesktop && (
              <Transition
                show={open}
                enter='transition duration-300 ease-in-out'
                enterFrom='opacity-0 translate-y--2'
                enterTo='opacity-100 translate-y-0'
                leave='transition duration-300 ease-in-out'
                leaveFrom='opacity-100 translate-y-0'
                leaveTo='opacity-0 translate-y--2'
              >
                <ListboxOptions options={options} listHeight={listHeight} />
              </Transition>
            )}
          </div>
        )}
      </Listbox>
    </div>
  );
};

SelectInput.propTypes = {
  selectedOption: PropTypes.object,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  options: PropTypes.array.isRequired,
  handleChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  listHeight: PropTypes.string,
  autoScroll: PropTypes.bool
};
